import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ProductCard.css';
import { useCart } from '../context/CartContext';
import { toggleWishlist, isWishlisted } from '../utils/localWishlist';

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);
  const [wished, setWished] = useState(false);

  useEffect(() => {
    if (product?.id) setWished(isWishlisted(product.id));
  }, [product]);

  useEffect(() => {
    const handler = () => {
      if (product?.id) setWished(isWishlisted(product.id));
    };
    window.addEventListener('wishlistUpdated', handler);
    return () => window.removeEventListener('wishlistUpdated', handler);
  }, [product]);

  if (!product) return null;

  // ---------- Get image URL ----------
  const getImageUrl = () => {
    if (product.primary_image) return product.primary_image;

    if (product.image) {
      if (product.image.startsWith('http://') || product.image.startsWith('https://')) {
        return product.image;
      }
      const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      return `${base}${product.image.startsWith('/') ? '' : '/'}${product.image}`;
    }

    if (product.images?.length) {
      const first = product.images[0].image;
      if (first.startsWith('http://') || first.startsWith('https://')) return first;
      const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      return `${base}${first.startsWith('/') ? '' : '/'}${first}`;
    }

    return 'https://via.placeholder.com/400x300?text=No+Image';
  };

  const imageUrl = getImageUrl();
  const discount =
    product.discount_percentage ||
    (product.old_price
      ? Math.round(((product.old_price - product.price) / product.old_price) * 100)
      : 0);

  const formatPrice = (v) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!product || product.stock === 0) return;
    addToCart(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    if (!product) return;
    const result = toggleWishlist(product);
    setWished(result.wished);
    window.dispatchEvent(new Event('wishlistUpdated'));
  };

  const shortDesc =
    product.short_description || product.description?.slice(0, 80) || '';

  return (
    <div className="product-card-modern">
      {/* Wishlist */}
      <button
        type="button"
        className={`wishlist-modern ${wished ? 'active' : ''}`}
        onClick={handleWishlist}
        aria-label="Wishlist"
      >
        {wished ? '❤️' : '🤍'}
      </button>

      {/* Discount Badge */}
      {discount > 0 && <div className="discount-modern">{discount}% OFF</div>}

      <Link
        to={`/product/${product.slug || product.id}`}
        className="image-link-modern"
      >
        <div className="image-wrapper-modern">
          <img
            src={imageUrl}
            alt={product.name}
            className="product-image-modern"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/400x300?text=Error';
            }}
          />
          {product.stock === 0 && (
            <div className="sold-out-modern">SOLD OUT</div>
          )}
        </div>
      </Link>

      <div className="product-info-modern">
        <h3 className="product-title-modern">{product.name}</h3>

        {shortDesc && <p className="product-desc-modern">{shortDesc}</p>}

        <div className="price-row-modern">
          <span className="current-price-modern">₹{formatPrice(product.price)}</span>
          {product.old_price && (
            <span className="old-price-modern">₹{formatPrice(product.old_price)}</span>
          )}
        </div>

        <button
          type="button"
          className={`cart-btn-modern ${added ? 'added' : ''}`}
          onClick={handleAddToCart}
          disabled={product.stock === 0}
        >
          {product.stock === 0
            ? 'OUT OF STOCK'
            : added
            ? '✓ ADDED'
            : 'ADD TO CART'}
        </button>
      </div>
    </div>
  );
}